import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate } from "react-router-dom";
import { Field, FieldArray, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { cpf as cpfValidator } from "cpf-cnpj-validator";
import format from "date-fns/format";
import InputMask from 'react-input-mask';
import { Container, Row, Col } from 'react-bootstrap';

import { FlightListTile } from "components/Safra/FlightListTile";
import Button from "components/Button";
import Footer from "components/Safra/Footer";
import { LoaderSearchImage } from "components/Loader";
import { ModalApp } from "components/Safra/Modal";

import { useApp } from "contexts/AppContext";
import { useAuth } from "contexts/Safra/AuthProvider";
import { useTranslation } from "contexts/Safra/TranslationContext";
import { setTravellersAirfareAction, checkoutAirfareAction } from "contexts/Safra/actions";
import { usaPontos } from "utils";

import {
    Container as StyledContainer,
    Line,
    SummaryBox,
    FormContainer
} from "./styles";

export default function PassageResume() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [selectedFlight, setSelectedFlight] = useState(null);
    const [passengerCounts, setPassengerCounts] = useState({ adults: 1, children: 0, infants: 0 });
    const [openModal, setOpenModal] = useState(false);
    const [modalMessage, setModalMessage] = useState("");

    const { t } = useTranslation();
    const { airfareDispatch, airfareState } = useApp();
    const { customerId } = useAuth();

    useEffect(() => {
        const flightData = localStorage.getItem('selected_flight');
        if (flightData) {
            setSelectedFlight(JSON.parse(flightData));
        }

        setPassengerCounts({
            adults: parseInt(localStorage.getItem('adults')) || 1,
            children: parseInt(localStorage.getItem('children')) || 0,
            infants: parseInt(localStorage.getItem('baby')) || 0
        });

        window.scrollTo(0, 0);
    }, []);

    if (!selectedFlight && !loading) {
        return <Navigate to="/passage-search-result" />;
    }

    const totalPassengers = passengerCounts.adults + passengerCounts.children + passengerCounts.infants;

    // Create initial travellers list
    const initialTravellers = [];
    for (let i = 0; i < passengerCounts.adults; i++) initialTravellers.push({ firstName: '', lastName: '', birthDate: '', gender: 'male', document: '', idType: 'CPF', type: 'ADT' });
    for (let i = 0; i < passengerCounts.children; i++) initialTravellers.push({ firstName: '', lastName: '', birthDate: '', gender: 'male', document: '', idType: 'CPF', type: 'CHD' });
    for (let i = 0; i < passengerCounts.infants; i++) initialTravellers.push({ firstName: '', lastName: '', birthDate: '', gender: 'male', document: '', idType: 'CPF', type: 'INF' });

    const initialValues = {
        travellers: initialTravellers
    };

    const validationSchema = Yup.object().shape({
        travellers: Yup.array().of(
            Yup.object().shape({
                firstName: Yup.string().required(t('safra.passage_resume.validation.required')).min(2, t('safra.passage_resume.validation.too_short')),
                lastName: Yup.string().required(t('safra.passage_resume.validation.required')).min(2, t('safra.passage_resume.validation.too_short')),
                birthDate: Yup.string().required(t('safra.passage_resume.validation.required')).matches(/^\d{2}\/\d{2}\/\d{4}$/, t('safra.passage_resume.validation.invalid_date')),
                document: Yup.string().required(t('safra.passage_resume.validation.required')).test('test-cpf', t('safra.passage_resume.validation.invalid_cpf'), (value) => cpfValidator.isValid(value))
            })
        )
    });

    const handleCheckout = async (values) => {
        setLoading(true);
        try {
            // Save travellers to context
            if (airfareDispatch) {
                airfareDispatch(setTravellersAirfareAction(values.travellers));
            }

            const checkout = await checkoutAirfareAction({
                customerId,
                offersAirfare: usaPontos() ? 'points' : 'cash',
                chooseId: airfareState.chooseId
            });

            if (checkout?.data && checkout?.hash) {
                window.parent.postMessage({
                    'message': 'start-checkout-aereo',
                    payload: checkout.data,
                    hash: checkout.hash
                }, '*');
                return;
            }
            setLoading(false);
        } catch (error) {
            setLoading(false);
            setModalMessage(t('safra.passage_resume.validation.error_processing'));
            setOpenModal(true);
        }
    };

    if (loading) return <LoaderSearchImage />;

    return (
        <StyledContainer>
            <p className="resume-title">{t('safra.passage_resume.title')}</p>

            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleCheckout}
            >
                {({ values, errors, touched, isValid, dirty }) => (
                    <Form>
                        <FormContainer>
                            <FieldArray name="travellers">
                                {() => (
                                    <div>
                                        {values.travellers.map((traveller, index) => (
                                            <div key={index} className="passenger-block">
                                                <span className="label">
                                                    {t('safra.passage_resume.passenger_label', { 
                                                        index: index + 1, 
                                                        type: t(`safra.passage_resume.types.${traveller.type}`) 
                                                    })}
                                                </span>
                                                <Row>
                                                    <Col md={6} className="mb-3">
                                                        <label className="form-label" htmlFor={`travellers.${index}.firstName`} style={{ color: 'var(--text-color)', fontFamily: 'var(--font-family-base)' }}>{t('safra.passage_resume.fields.first_name')}</label>
                                                        <Field
                                                            name={`travellers.${index}.firstName`}
                                                            id={`travellers.${index}.firstName`}
                                                            placeholder={t('safra.passage_resume.fields.placeholder_first_name')}
                                                            className={`form-control ${errors.travellers?.[index]?.firstName && touched.travellers?.[index]?.firstName ? 'is-invalid' : ''}`}
                                                            aria-label={`${index + 1}º passageiro - ${t('safra.passage_resume.fields.first_name')}`}
                                                            aria-required="true"
                                                            aria-invalid={errors.travellers?.[index]?.firstName ? 'true' : 'false'}
                                                            aria-describedby={`travellers.${index}.firstName-help travellers.${index}.firstName-error`}
                                                        />
                                                        {errors.travellers?.[index]?.firstName && touched.travellers?.[index]?.firstName && (
                                                            <div id={`travellers.${index}.firstName-error`} className="invalid-feedback" role="alert">{errors.travellers[index].firstName}</div>
                                                        )}
                                                        <small className="form-text text-muted" id={`travellers.${index}.firstName-help`}>{t('safra.passage_resume.fields.help_first_name')}</small>
                                                    </Col>
                                                    <Col md={6} className="mb-3">
                                                        <label className="form-label" htmlFor={`travellers.${index}.lastName`} style={{ color: 'var(--text-color)', fontFamily: 'var(--font-family-base)' }}>{t('safra.passage_resume.fields.last_name')}</label>
                                                        <Field
                                                            name={`travellers.${index}.lastName`}
                                                            id={`travellers.${index}.lastName`}
                                                            placeholder={t('safra.passage_resume.fields.placeholder_last_name')}
                                                            className={`form-control ${errors.travellers?.[index]?.lastName && touched.travellers?.[index]?.lastName ? 'is-invalid' : ''}`}
                                                            aria-label={`${index + 1}º passageiro - ${t('safra.passage_resume.fields.last_name')}`}
                                                            aria-required="true"
                                                            aria-invalid={errors.travellers?.[index]?.lastName ? 'true' : 'false'}
                                                            aria-describedby={`travellers.${index}.lastName-help travellers.${index}.lastName-error`}
                                                        />
                                                        {errors.travellers?.[index]?.lastName && touched.travellers?.[index]?.lastName && (
                                                            <div id={`travellers.${index}.lastName-error`} className="invalid-feedback" role="alert">{errors.travellers[index].lastName}</div>
                                                        )}
                                                        <small className="form-text text-muted" id={`travellers.${index}.lastName-help`}>{t('safra.passage_resume.fields.help_last_name')}</small>
                                                    </Col>
                                                    <Col md={4} className="mb-3">
                                                        <label className="form-label" htmlFor={`travellers.${index}.birthDate`} style={{ color: 'var(--text-color)', fontFamily: 'var(--font-family-base)' }}>{t('safra.passage_resume.fields.birth_date')}</label>
                                                        <InputMask
                                                            mask="99/99/9999"
                                                            value={values.travellers[index].birthDate}
                                                            onChange={(e) => values.travellers[index].birthDate = e.target.value}
                                                            aria-label={`${index + 1}º passageiro - ${t('safra.passage_resume.fields.birth_date')}`}
                                                            aria-required="true"
                                                            aria-invalid={errors.travellers?.[index]?.birthDate ? 'true' : 'false'}
                                                            aria-describedby={`travellers.${index}.birthDate-error`}
                                                        >
                                                            {(inputProps) => (
                                                                <Field
                                                                    {...inputProps}
                                                                    id={`travellers.${index}.birthDate`}
                                                                    name={`travellers.${index}.birthDate`}
                                                                    inputMode="numeric"
                                                                    placeholder={t('safra.passage_resume.fields.placeholder_birth_date')}
                                                                    className={`form-control ${errors.travellers?.[index]?.birthDate && touched.travellers?.[index]?.birthDate ? 'is-invalid' : ''}`}
                                                                />
                                                            )}
                                                        </InputMask>
                                                        {errors.travellers?.[index]?.birthDate && touched.travellers?.[index]?.birthDate && (
                                                            <div id={`travellers.${index}.birthDate-error`} className="invalid-feedback" role="alert">{errors.travellers[index].birthDate}</div>
                                                        )}
                                                    </Col>
                                                    <Col md={4} className="mb-3">
                                                        <label className="form-label" htmlFor={`travellers.${index}.gender`} style={{ color: 'var(--text-color)', fontFamily: 'var(--font-family-base)' }}>{t('safra.passage_resume.fields.gender')}</label>
                                                        <Field
                                                            as="select"
                                                            name={`travellers.${index}.gender`}
                                                            id={`travellers.${index}.gender`}
                                                            className="form-control"
                                                            aria-label={`${index + 1}º passageiro - ${t('safra.passage_resume.fields.gender')}`}
                                                            aria-required="true"
                                                        >
                                                            <option value="male">{t('safra.passage_resume.fields.genders.male')}</option>
                                                            <option value="female">{t('safra.passage_resume.fields.genders.female')}</option>
                                                        </Field>
                                                    </Col>
                                                    <Col md={4} className="mb-3">
                                                        <label className="form-label" htmlFor={`travellers.${index}.document`} style={{ color: 'var(--text-color)', fontFamily: 'var(--font-family-base)' }}>{t('safra.passage_resume.fields.document')}</label>
                                                        <InputMask
                                                            mask="999.999.999-99"
                                                            value={values.travellers[index].document}
                                                            onChange={(e) => values.travellers[index].document = e.target.value}
                                                            aria-label={`${index + 1}º passageiro - ${t('safra.passage_resume.fields.document')}`}
                                                            aria-required="true"
                                                            aria-invalid={errors.travellers?.[index]?.document ? 'true' : 'false'}
                                                            aria-describedby={`travellers.${index}.document-error`}
                                                        >
                                                            {(inputProps) => (
                                                                <Field
                                                                    {...inputProps}
                                                                    id={`travellers.${index}.document`}
                                                                    name={`travellers.${index}.document`}
                                                                    inputMode="numeric"
                                                                    placeholder="000.000.000-00"
                                                                    className={`form-control ${errors.travellers?.[index]?.document && touched.travellers?.[index]?.document ? 'is-invalid' : ''}`}
                                                                />
                                                            )}
                                                        </InputMask>
                                                        {errors.travellers?.[index]?.document && touched.travellers?.[index]?.document && (
                                                            <div id={`travellers.${index}.document-error`} className="invalid-feedback" role="alert">{errors.travellers[index].document}</div>
                                                        )}
                                                    </Col>
                                                </Row>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </FieldArray>
                        </FormContainer>

                        <Line />

                        <SummaryBox>
                            <p className="title-summary">{t('safra.passage_resume.summary.title')}</p>
                            <FlightListTile data={selectedFlight} simpleView />

                            <div className="total-row">
                                <span className="total-label">{t('safra.passage_resume.summary.subtotal')}</span>
                                <span className="total-value">{selectedFlight.totalPoints.toLocaleString('pt-BR')} {t('safra.passage_resume.summary.points')}</span>
                            </div>
                            <div className="item-row">
                                <span className="item-label">{t('safra.passage_resume.summary.taxes')}</span>
                                <div className="dots" />
                                <span className="item-value">{selectedFlight.tax.toLocaleString('pt-BR')} {t('safra.passage_resume.summary.points')}</span>
                            </div>
                            <div className="total-row">
                                <span className="total-label">{t('safra.passage_resume.summary.total')}</span>
                                <span className="total-value">{(selectedFlight.totalPoints + selectedFlight.tax).toLocaleString('pt-BR')} {t('safra.passage_resume.summary.points')}</span>
                            </div>
                        </SummaryBox>

                        <div className="mt-4">
                            <Button
                                type="submit"
                                disabled={!isValid || !dirty}
                                style={{
                                    backgroundColor: (!isValid || !dirty) ? "var(--button-disabled-color)" : "var(--primary-color)",
                                    border: "none",
                                    opacity: (!isValid || !dirty) ? 0.5 : 1,
                                    transition: "background-color 0.2s ease",
                                }}
                                aria-label="Continuar para checkout"
                            >
                                {t('common.continue')}
                            </Button>
                        </div>
                    </Form>
                )}
            </Formik>

             <ModalApp show={openModal} handleModal={() => setOpenModal(false)}>
                <p>{modalMessage}</p>
            </ModalApp>

            <Footer />
        </StyledContainer>
    );
}
